
<script>
    import EventHub from '/src/Util/EventHub.js';
    import { v4 as uuidv4 } from 'uuid';
    import { useEditorStore } from '/src/stores/EditorStore';
    import { toast } from 'vue3-toastify';
    import Swal from 'sweetalert2'
    import propertycoloreditor from '/src/components/propertycoloreditor.vue';
    import propertytogglededitor from '/src/components/propertytogglededitor.vue';
    import propertyiconeditor from '/src/components/propertyiconeditor.vue';
    import propertyrangeeditor from '/src/components/propertyrangeeditor.vue'; 
    import propertybaseeditors from '/src/components/propertybaseeditors.vue';

    import omnieditor from '/src/components/omnieditor.vue';
    import axios from '/src/components/NovaAxios.js';

    const NOTY_HELPER_CLASS = 'noty-html-helper-panel';
    let notySourceDialogObserver = null;

    const ensureNotySourceDialogHelper = () => {
        if (typeof document === 'undefined' || typeof window === 'undefined') {
            return;
        }

        if (notySourceDialogObserver) {
            return;
        }

        const processNode = (node) => {
            if (!(node instanceof HTMLElement)) {
                return;
            }

            if (node.classList.contains('tox-dialog')) {
                enhanceSourceDialog(node);
                return;
            }

            node.querySelectorAll?.('.tox-dialog').forEach(enhanceSourceDialog);
        };

        notySourceDialogObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                mutation.addedNodes.forEach(processNode);
            }
        });

        notySourceDialogObserver.observe(document.body, { childList: true, subtree: true });
        document.querySelectorAll('.tox-dialog').forEach(enhanceSourceDialog);
    };

    const enhanceSourceDialog = (dialogEl) => {
        if (!isSourceDialog(dialogEl) || dialogEl.querySelector(`.${NOTY_HELPER_CLASS}`)) {
            return;
        }

        const formGroup = dialogEl.querySelector('.tox-dialog__body .tox-form__group');
        const textarea = dialogEl.querySelector('textarea[name="code"], textarea[aria-label="Source code"], textarea.tox-textarea');

        if (!formGroup || !textarea) {
            return;
        }
        const helperPanel = buildNotyHelperPanel(textarea);
        formGroup.parentElement?.insertBefore(helperPanel, formGroup);

        dialogEl.classList.add('noty-source-dialog');
        const textareaWrap = dialogEl.querySelector('.tox-textarea-wrap');
        textareaWrap?.classList.add('noty-source-textarea-wrap');
    };

    const isSourceDialog = (dialogEl) => {
        const title = dialogEl.querySelector('.tox-dialog__title')?.textContent?.trim().toLowerCase();
        const hasCodeField = dialogEl.querySelector('textarea[name="code"], textarea[aria-label="Source code"], textarea.tox-textarea');
        return title === 'source code' || Boolean(hasCodeField);
    };

    const setStatusMessage = (statusEl, message, isError = false) => {
        statusEl.textContent = message;
        statusEl.classList.toggle('noty-html-helper__status--error', Boolean(isError));
    };

    const buildLabel = (text) => {
        const label = document.createElement('label');
        label.className = 'noty-html-helper__label';
        label.textContent = text;
        return label;
    };

    const buildNotyHelperPanel = (editorTextarea) => {
        const panel = document.createElement('div');
        panel.className = `${NOTY_HELPER_CLASS} tox-form__group`;

        const card = document.createElement('div');
        card.className = 'noty-html-helper-card';

        const promptInput = document.createElement('textarea');
        promptInput.rows = 2;
        promptInput.className = 'noty-html-helper__input tox-textarea';
        promptInput.placeholder = 'e.g. “Make this two columns with green check icons.”';

        const suggestionsWrapper = document.createElement('div');
        suggestionsWrapper.className = 'noty-html-helper__suggestions';

        const suggestionsSelect = document.createElement('select');
        suggestionsSelect.className = 'noty-html-helper__select';
        const defaultOption = document.createElement('option');
        defaultOption.value = '';
        defaultOption.textContent = 'Need ideas? Pick a sample prompt…';
        suggestionsSelect.appendChild(defaultOption);

        const samplePrompts = [
            'Give me a hero section for my mobile notary agency with the headline “On-Demand Signing Experts” plus two action buttons so clients can book or call us. Show a friendly photo on the right.',
            'Lay out three service cards for Field Inspections, Apostille Runs, and Courier Dispatch. Each card should include a picture, a short benefit, and a “Learn more” link.',
            'Add two stacked customer testimonials from real estate partners, complete with their photo, name, and role.',
            'Create a simple pricing section with “Basic Signing” and “Premium Concierge” plans. Each needs the price, key highlights, and a call-to-action button.',
            'Insert a bold banner inviting visitors to request a quote for courier or apostille work, including a headline, one-line promise, and a button labeled “Request a quote”.',
            'Build a four-question FAQ covering scheduling, rush jobs, payment terms, and service areas.',
            'Create a team spotlight for our client success crew with four headshots, names, titles, and short taglines.'
        ];

        samplePrompts.forEach((text, index) =>
        {
            const option = document.createElement('option');
            option.value = text;
            option.textContent = `Example ${index + 1}: ${text.slice(0, 60)}…`;
            suggestionsSelect.appendChild(option);
        });

        suggestionsSelect.addEventListener('change', () =>
        {
            if (!suggestionsSelect.value)
            {
                return;
            }

            promptInput.value = suggestionsSelect.value;
            suggestionsSelect.value = '';
        });

        suggestionsWrapper.appendChild(suggestionsSelect);

        const actions = document.createElement('div');
        actions.className = 'noty-html-helper__actions';

        const askButton = document.createElement('button');
        askButton.type = 'button';
        askButton.className = 'noty-html-helper__button';
        askButton.textContent = 'Ask Noty AI';

        const status = document.createElement('div');
        status.className = 'noty-html-helper__status';
        status.textContent = 'Noty can polish or restructure this HTML for you.';

        askButton.addEventListener('click', async () => {
            const instructions = promptInput.value.trim();
            if (!instructions) {
                setStatusMessage(status, 'Describe the change you want before asking Noty.', true);
                promptInput.focus();
                return;
            }

            askButton.disabled = true;
            setStatusMessage(status, 'Asking Noty…', false);

            try {
                const { data } = await axios.post('/ai/html/generate', {
                    brief: instructions,
                    existingHtml: editorTextarea.value
                });

                const updatedHtml = data?.html;
                if (typeof updatedHtml === 'string' && updatedHtml.trim().length > 0) {
                    editorTextarea.value = updatedHtml;
                    editorTextarea.dispatchEvent(new Event('input', { bubbles: true }));
                    setStatusMessage(status, 'Updated HTML. Review and click Save when ready.', false);
                    toast.success("Noty is done updating your HTML. Click 'Save' to apply it.", {
                        autoClose: 5000,
                        pauseOnHover: true,
                        closeOnClick: true
                    });
                    promptInput.value = '';
                }
                else {
                    setStatusMessage(status, 'No HTML was returned. Please try again.', true);
                }
            }
            catch (error) {
                const message = error?.response?.data?.error ?? 'Unable to update HTML right now.';
                setStatusMessage(status, message, true);
            }
            finally {
                askButton.disabled = false;
            }
        });

        actions.appendChild(askButton);
        card.appendChild(buildLabel('Ask Noty HTML Helper'));
        card.appendChild(suggestionsWrapper);
        card.appendChild(promptInput);
        card.appendChild(actions);
        card.appendChild(status);

        panel.appendChild(card);

        return panel;
    };

    /*<vue3-icon-picker @update:renderedIcon="OnIconChange" :renderedIcon="IconPick" :renderedColor="IconColor" :renderedSize="IconSize" />*/

    export default {
        name: 'ContentBlockBase',
        components: {
            omnieditor,
            'propertycoloreditor': propertycoloreditor,
            'propertytogglededitor': propertytogglededitor,
            'propertyiconeditor': propertyiconeditor,
            'propertyrangeeditor': propertyrangeeditor,
            'propertybaseeditors': propertybaseeditors
        },
        props:
            [
            "iseditmode",
            "filepond",
            "componenttype", // From EditorStore
            "blockprops", // From EditorStore
            "blockrenderindex" // From render loop in EditorPageRoot, based on EditorStore array position
        ],
        data() {
            return {
                Store: null,
                MaxTinyMCEContentFilesize: 2097152,//2mb
                ContentBlockBaseProps:
                {
                    Blurred: false,
                    Style: '', // CSS style tag
                    Class: 'nova-content-block' // class tag
                },
                AdditionalCfg: ''
            };
        },
        methods:
        {
            ClearTinyMceFileInputs() {
                const fileInputs = document.querySelectorAll('.tox-dropzone input[type="file"]');

                // Loop through the file input elements and reset them
                fileInputs.forEach(input => {
                    input.value = ''; // Clear the selected files
                });
            },
            async ImageUploadHandler(blobInfo, progress) {
                try {                    

                    return await new Promise((resolve, reject) => {

                        const vm = this;
                        vm.filepond.removeFiles({ revert: true });

                        var blob = blobInfo.blob();

                        if (blob.size > vm.MaxTinyMCEContentFilesize) {
                            reject({ message: "Max file size is 2MB", remove: true });
                            return;
                        }

                        var filename = blobInfo.filename();

                        vm.filepond.removeFiles();
                        vm.filepond.addFile(blob);
                        vm.filepond.onprocessfile = (error, file) => {
                            vm.Store.OverrideFileUpload = false;
                            if (error) {
                                return;// reject handled by onerror handler, below
                            }
                            var url = `https://mediadocs.notarylaunchpad.com/tasset/${file.serverId.replace(">", "/")}?v=${Date.now()}_`;
                            resolve(url);
                        }

                        vm.filepond.onprocessfileprogress = (file, amt) => {
                            var prog = 0;
                            if (amt)
                            {
                                prog = Math.round(amt * 100);
                            }
                            progress(prog);
                        }

                        vm.filepond.onerror = (error, file, status) => {
                            debugger;
                            if (error.code == 409)
                            {
                                try
                                {
                                    var errDat = {};
                                    if (error.body)
                                    {
                                        var activeEditor = tinyMCE.activeEditor;
                                        debugger;
                                        errDat = JSON.parse(error.body)
                                        var existingUrl = `https://mediadocs.notarylaunchpad.com/tasset/${errDat.DiskName}?v=${Date.now()}_`;
                                        Swal.fire({
                                            title: 'Overwrite?',
                                            html: 'File with that name already exists. Overwrite it globally?<br/><br/><small class="text-muted" style="font-size:12px;">Overwriting the file causes it to be replaced anywhere it may already be referenced in your live website (no publish required). Choose <strong>Use Existing</strong> to reuse the file shown above. The safest option is to <strong>Cancel</strong>, rename your file and re-upload to avoid any unintended side-effects.</small>',
                                            imageUrl: existingUrl,
                                            imageAlt: 'Existing File',
                                            showDenyButton: true,
                                            showCancelButton: true,
                                            confirmButtonText: 'Use Existing (above)',
                                            denyButtonText: `Overwrite this file!`,
                                        }).then((result) => {
                                            if (result.isConfirmed) {
                                                resolve(existingUrl);
                                            } else if (result.isDenied) {
                                                vm.Store.OverrideFileUpload = true;
                                                vm.filepond.processFiles();
                                            } else {
                                                vm.ClearTinyMceFileInputs();
                                                reject({ message: `${status.main}. Upload Canceled.`, remove: true });
                                            }

                                            tinyMCE?.activeEditor.hide()
                                            tinyMCE?.activeEditor.show()
                                        })
                                    }
                                } catch (e) {}

                            }                            
                            
                        }

                        vm.filepond.processFiles();
                    });
                   
                } catch (error) {
                    // Handle the error appropriately, e.g., show an error message.
                    throw error;
                }
            },

            HideEditProperties: function () {
                var id = '#editor' + this.BlockProps.UId;
                var modalEl = document.querySelector(id);
                if (modalEl == null) {
                    return;
                }
                var modal = bootstrap.Modal.getOrCreateInstance(modalEl); // Returns a Bootstrap modal instance
                modal.hide();
            },

            EditProperties: function () {
                alert("There are no properties to edit on this block.");
            }
        },
        computed: {            
             ContainerPadding: function () {
                 return {
                     'padding-top': this.BlockProps.PaddingTop + "px",
                     'padding-bottom': this.BlockProps.PaddingBottom + "px"
                 };
                 //`pb-${this.BlockProps.PaddingBottom} pt-${this.BlockProps.PaddingTop}`;
             },
            EditorConfig: function () {
                var config =
                {
                    plugins: [
                        'advlist',
                        'autolink',
                        'lists',
                        'link',
                        'image',
                        'media',
                        'table',
                        'code',
                        'fullscreen',
                      
                    ],
                    convert_urls: false,
                    toolbar: 'undo redo | bold italic underline removeformat | font  forecolor backcolor | link unlink | image media | alignment | lists | table | code ',
                    toolbar_groups: {
                        lists: {
                            icon: 'checklist',
                            tooltip: 'Lists',
                            items: 'link unlink'
                        },
                        alignment: {
                            icon: 'align-justify',
                            tooltip: 'Alignments',
                            items: 'alignleft aligncenter alignright alignjustify'
                        },
                        font: {
                            icon: 'change-case',
                            tooltip: 'Font Options',
                            items: 'fontfamily fontsize'
                        }
                    },
                    paste_data_images: false,
                    images_file_types: 'jpeg,jpg,jpe,png,gif',
                    file_picker_types: "image",
                    automatic_uploads: true,
                    images_upload_handler: this.ImageUploadHandler,
                    toolbar_persist: false,
                    inline: true,
                    menubar: false,
                    fixed_toolbar_container: '#toolbar',
                    setup: () => {
                        ensureNotySourceDialogHelper();
                    }
                }

                return config;
            },
            
            HasPropertyEditor: function () {
                return true;
            },
            BlockRenderIndex: function ()
            {
                return this.blockrenderindex;
            },
            ComponentType: function () {
                return this.componenttype;
            },
            BlockProps: function ()
            {
                return this.blockprops; // From Editor.js
            }
        },
        created() {
            if(this.BlockProps.UId == "00000000-0000-0000-0000-000000000000"){
                this.BlockProps.UId = uuidv4();
            }
            this.Store = useEditorStore();

            //EventHub.$on('ContentBlockBase', this.showSettings);       
            EventHub.$on('OnEditProperties-' + this.BlockProps.UId, this.EditProperties);      
        },
        beforeUnmount() {
            //EventHub.$off('ContentBlockBase', this.showSettings);         
        },
        mounted() {
            if (typeof document !== 'undefined') {
                const styleId = 'tiny-inline-boundary-override';
                if (!document.getElementById(styleId)) {
                    const styleEl = document.createElement('style');
                    styleEl.id = styleId;
                    styleEl.textContent = 'body .mce-content-body [data-mce-selected], body .mce-content-body [data-mce-selected="inline-boundary"], #tinymce .mce-content-body [data-mce-selected], #tinymce .mce-content-body [data-mce-selected="inline-boundary"] { background-color: inherit !important; color: inherit !important; }';
                    document.head.appendChild(styleEl);
                }

                if (!window.__tinyInlineSelectionCleaner) {
                    const observer = new MutationObserver((mutations) => {
                        for (const mutation of mutations) {
                            if (mutation.type === 'attributes' && mutation.attributeName === 'data-mce-selected') {
                                mutation.target.removeAttribute('data-mce-selected');
                            }
                        }
                    });

                    observer.observe(document.body, {
                        attributes: true,
                        subtree: true,
                        attributeFilter: ['data-mce-selected']
                    });

                    window.__tinyInlineSelectionCleaner = observer;
                }
            }
        }
    }

 
</script>
<style>
    .swal2-image {
        max-width: 200px;
        max-height: 200px;
        border: dashed 1px maroon;
        padding: 5px;
 
    }

    .tox .tox-form__group.noty-html-helper-panel {
        padding: 0;
        margin-bottom: 16px !important;
    }

    .tox .noty-html-helper-card {
        padding: 12px 14px;
        border: 1px solid rgba(13, 49, 98, 0.25);
        border-radius: 12px;
        background: linear-gradient(135deg, #fefefe 0%, #f3f8ff 70%);
        box-shadow: 0 10px 25px rgba(8, 29, 71, 0.12);
        background-clip: padding-box;
    }

    .tox .noty-html-helper__label {
        display: block;
        font-size: 0.8rem;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: #0b3a6f;
        margin-bottom: 8px;
    }

    .tox .noty-html-helper__input {
        width: 100%;
        resize: vertical;
        min-height: 52px;
        margin-bottom: 10px;
    }

    .tox .noty-html-helper__suggestions {
        margin-bottom: 8px;
    }

    .tox .noty-html-helper__select {
        width: 100%;
        border-radius: 6px;
        border: 1px solid rgba(13, 49, 98, 0.3);
        padding: 6px 10px;
        font-size: 0.85rem;
    }

    .tox .noty-html-helper__actions {
        margin-bottom: 6px;
    }

    .tox .noty-html-helper__button {
        border: none;
        border-radius: 999px;
        padding: 6px 16px;
        background: #0f9d58;
        color: #fff;
        font-weight: 600;
        cursor: pointer;
        font-size: 0.8rem;
        box-shadow: 0 5px 14px rgba(15, 157, 88, 0.3);
    }

    .tox .noty-html-helper__button:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        box-shadow: none;
    }

    .tox .noty-html-helper__status {
        font-size: 0.72rem;
        color: #4b5563;
        line-height: 1.2;
    }

    .tox .noty-html-helper__status--error {
        color: #b3261e;
    }
</style>
    .tox .tox-dialog.noty-source-dialog {
        min-height: 620px;
        max-height: 90vh;
    }

    .tox .tox-dialog.noty-source-dialog .noty-source-textarea-wrap .tox-textarea {
        border: 2px solid #000 !important;
        border-color: #000 !important;
        box-shadow: 0 0 0 1px #000 !important;
        min-height: 420px !important;
        border-radius: 6px;
    }
